/* eslint-disable jsx-a11y/anchor-is-valid */
import { useEffect, useRef, useState } from 'react'
import { Outlet, Route, Routes, useNavigate } from 'react-router'
import { Registration } from './components/Registration'
import { ForgotPassword } from './components/ForgotPassword'
import { CbeeUserProfile, getStoredCbeeUserProfile, Login, saveStoredCbeeUserProfile } from './components/Login'
import { toAbsoluteUrl } from '../../../_metronic/helpers'
import { AutoLogin } from './components/AutoLogin'
import { ThemeModeProvider } from '../../../_metronic/partials/layout/theme-mode/ThemeModeProvider'
import { DEFAULT_AUTHENTICATED_ROUTE } from '../../routing/constants'
import { useAuth } from './core/Auth'
import { ensureAuthSessionStartedAt, getAuthRoles, getAuthSessionStartedAt } from './core/AuthHelpers'
import { UserModel } from './core/_models'
import { getUserProfile } from './core/_requests'
import './auth-cbee.css'

const cbbeeDesignThemeStorageKey = 'cbee-design-theme'
type CbbeeDesignTheme = 'bumblebee' | 'azure' | 'shieldmaiden'

const cbbeeDesignThemeOptions: {id: CbbeeDesignTheme; label: string}[] = [
  {id: 'bumblebee', label: 'BumbleBee'},
  {id: 'azure', label: 'Azure'},
  {id: 'shieldmaiden', label: 'ShieldMaiden'},
]

const getStoredCbbeeDesignTheme = (): CbbeeDesignTheme => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return 'bumblebee'
  }

  const storedTheme = window.localStorage.getItem(cbbeeDesignThemeStorageKey)
  return cbbeeDesignThemeOptions.some((option) => option.id === storedTheme)
    ? (storedTheme as CbbeeDesignTheme)
    : 'bumblebee'
}

const applyCbbeeDesignTheme = (theme: CbbeeDesignTheme) => {
  if (typeof document === 'undefined') {
    return
  }

  document.documentElement.setAttribute('data-cbbee-theme', theme)
}

const getTextValue = (value: unknown) => {
  if (typeof value === 'number') {
    return String(value)
  }

  return typeof value === 'string' ? value.trim() : ''
}

const getProfileImageSource = (profile: CbeeUserProfile) =>
  getTextValue(profile.imageUrl) || getTextValue(profile.image)

export const getUserDisplayName = (user: UserModel | undefined, profile: CbeeUserProfile) => {
  const profileName = [profile.firstName, profile.lastName].map(getTextValue).filter(Boolean).join(' ')

  return (
    getTextValue(user?.fullName) ||
    getTextValue(user?.fullname) ||
    getTextValue(user?.username) ||
    getTextValue(user?.userName) ||
    getTextValue(user?.email) ||
    profileName ||
    'Kullanıcı'
  )
}

export const getUserInitials = (displayName: string) => {
  const parts = displayName.split(/\s+/).filter(Boolean)

  if (parts.length === 0) {
    return 'CB'
  }

  return parts
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join('')
    .toUpperCase()
}

export const CbeeProfileAvatar = ({
  profile,
  displayName,
  initials,
}: {
  profile: CbeeUserProfile
  displayName: string
  initials: string
}) => {
  const imageSource = getProfileImageSource(profile)
  const [visibleImageSource, setVisibleImageSource] = useState('')
  const [imageAttempted, setImageAttempted] = useState(false)

  useEffect(() => {
    if (!imageSource) {
      if (!visibleImageSource) {
        setImageAttempted(true)
      }
      return
    }

    if (imageSource === visibleImageSource) {
      setImageAttempted(true)
      return
    }

    let active = true
    setImageAttempted(false)
    const image = new Image()

    image.onload = () => {
      if (!active) {
        return
      }

      setVisibleImageSource(imageSource)
      setImageAttempted(true)
    }

    image.onerror = () => {
      if (!active) {
        return
      }

      setImageAttempted(true)
    }

    image.src = imageSource

    return () => {
      active = false
    }
  }, [imageSource, visibleImageSource])

  if (visibleImageSource) {
    return <img className='cbee-auth-user-logo-image' alt={displayName} src={visibleImageSource} />
  }

  if (imageSource && !imageAttempted) {
    return <span className='cbee-auth-user-logo-loading' aria-label={`${displayName} fotografi yukleniyor`} />
  }

  return (
    <span className='cbee-auth-user-logo-initials' aria-label={displayName}>
      {initials}
    </span>
  )
}

export const useStoredCbeeUserProfile = (currentUser?: UserModel) => {
  const [profile, setProfile] = useState<CbeeUserProfile>(() => getStoredCbeeUserProfile())
  const currentUserId = currentUser?.id

  useEffect(() => {
    const syncProfile = () => setProfile(getStoredCbeeUserProfile())

    window.addEventListener('cbee-profile-updated', syncProfile)
    window.addEventListener('storage', syncProfile)
    return () => {
      window.removeEventListener('cbee-profile-updated', syncProfile)
      window.removeEventListener('storage', syncProfile)
    }
  }, [])

  useEffect(() => {
    if (!currentUserId) {
      return
    }

    let active = true

    getUserProfile()
      .then(({data}) => {
        if (!active) {
          return
        }

        const storedProfile = getStoredCbeeUserProfile()
        const stableImage = data.imageUrl || data.image || storedProfile.imageUrl || storedProfile.image
        const nextProfile = {
          ...storedProfile,
          firstName: data.firstName || storedProfile.firstName,
          lastName: data.lastName || storedProfile.lastName,
          roleLevel: data.roleLevel || storedProfile.roleLevel,
          positionName: data.positionName || storedProfile.positionName,
          image: stableImage,
          imageUrl: data.imageUrl || storedProfile.imageUrl || stableImage,
        }
        saveStoredCbeeUserProfile(nextProfile)
        setProfile(nextProfile)
      })
      .catch((error: any) => {
        if (error?.response?.status !== 404) {
          console.error('CBEE PROFILE LOAD ERROR', error)
        }
      })

    return () => {
      active = false
    }
  }, [currentUserId])

  return profile
}

const formatSessionDuration = (startedAt: number | undefined, now: number) => {
  if (!startedAt) {
    return '0 sa 00 dk'
  }

  const totalMinutes = Math.max(0, Math.floor((now - startedAt) / 60000))
  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  return `${hours} sa ${String(minutes).padStart(2, '0')} dk`
}

const readAuthSessionStartedAt = (authToken?: string) => {
  if (!authToken) {
    return undefined
  }

  return getAuthSessionStartedAt() || ensureAuthSessionStartedAt()
}

const useAuthSessionDuration = (authToken?: string) => {
  const [now, setNow] = useState(() => Date.now())
  const [startedAt, setStartedAt] = useState<number | undefined>(() => readAuthSessionStartedAt(authToken))

  useEffect(() => {
    const refreshDuration = () => {
      setStartedAt(readAuthSessionStartedAt(authToken))
      setNow(Date.now())
    }

    refreshDuration()
    const intervalId = window.setInterval(refreshDuration, 30000)
    return () => window.clearInterval(intervalId)
  }, [authToken])

  return formatSessionDuration(startedAt, now)
}

export const AuthUserSummary = ({
  profile,
  onUserOperationsClick,
}: {
  profile: CbeeUserProfile
  onUserOperationsClick?: () => void
}) => {
  const {auth, currentUser, logout} = useAuth()
  const navigate = useNavigate()
  const displayName = getUserDisplayName(currentUser, profile)
  const sessionDuration = useAuthSessionDuration(auth?.token)
  const authRoles = getAuthRoles()
  const roleNames = [
    ...(currentUser?.roleNames || []),
    getTextValue(currentUser?.roleName),
    getTextValue(currentUser?.role),
    ...(authRoles?.roles || []).map((role) => getTextValue(role.role)),
  ].filter(Boolean)
  const uniqueRoleNames = Array.from(new Set(roleNames))
  const positionName =
    getTextValue(profile.positionName) ||
    getTextValue(currentUser?.title) ||
    getTextValue(currentUser?.applicationUserTitle)
  const detailItems = [
    {label: 'Pozisyon adı', value: positionName || 'Pozisyon adı girilmedi'},
    {label: 'Rol', value: uniqueRoleNames.join(', ')},
    {label: 'Aktif süre', value: sessionDuration},
  ].filter((item) => item.value)

  const handleLogout = () => {
    logout()
    navigate('/auth/login', {replace: true})
  }

  return (
    <div className='cbee-auth-user-panel'>
      <div className='cbee-auth-form-heading cbee-auth-user-heading'>
        <span>Oturum bilgileri</span>
      </div>

      <div className='cbee-auth-user-title'>
        <div className='cbee-auth-profile-status-line'>
          <span>Mevcut Aktif Profil</span>
          <button type='button' className='cbee-auth-active-indicator' aria-label='Profil aktif'>
            Aktif
          </button>
        </div>
        <strong>{displayName}</strong>
      </div>

      <div className='cbee-auth-user-details' aria-label='Kullanıcı bilgileri'>
        {detailItems.map((item) => (
          <div className='cbee-auth-user-detail' key={item.label}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>

      <div className='cbee-auth-user-actions'>
        <button
          type='button'
          className='btn btn-lg cbee-auth-submit w-100'
          onClick={() => navigate(DEFAULT_AUTHENTICATED_ROUTE)}
        >
          <span>Sisteme devam et</span>
          <i className='bi bi-arrow-right-short'></i>
        </button>
        {onUserOperationsClick && (
          <button type='button' className='cbee-auth-secondary-button' onClick={onUserOperationsClick}>
            <i className='bi bi-person-gear'></i>
            <span>Kullanıcı işlemleri</span>
          </button>
        )}
        <button type='button' className='cbee-auth-logout-button' onClick={handleLogout}>
          <i className='bi bi-box-arrow-right'></i>
          <span>Çıkış yap</span>
        </button>
      </div>
    </div>
  )
}

const AuthLayout = () => {
  const [designTheme] = useState<CbbeeDesignTheme>(() => getStoredCbbeeDesignTheme())
  const [userOperationsOpen, setUserOperationsOpen] = useState(false)
  const sidebarRef = useRef<HTMLElement | null>(null)
  const profileEditorRef = useRef<HTMLElement | null>(null)
  const {currentUser} = useAuth()
  const profile = useStoredCbeeUserProfile(currentUser)
  const displayName = getUserDisplayName(currentUser, profile)
  const initials = getUserInitials(displayName)

  useEffect(() => {
    document.body.classList.add('cbee-auth-body')
    return () => {
      document.body.classList.remove('cbee-auth-body')
    }
  }, [])

  useEffect(() => {
    applyCbbeeDesignTheme(designTheme)

    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(cbbeeDesignThemeStorageKey, designTheme)
    }
  }, [designTheme])

  useEffect(() => {
    sidebarRef.current?.scrollTo({top: 0, left: 0})
  }, [currentUser?.id, userOperationsOpen])

  useEffect(() => {
    if (userOperationsOpen) {
      profileEditorRef.current?.scrollTo({top: 0, left: 0})
    }
  }, [userOperationsOpen])

  return (
    <ThemeModeProvider>
      <div className='cbee-auth-page'>
        <aside ref={sidebarRef} className='cbee-auth-sidebar' aria-label='C&Bee giris menusu'>
          <div className={`cbee-auth-logo-card ${currentUser ? 'is-user' : ''}`}>
            {currentUser ? (
              <CbeeProfileAvatar profile={profile} displayName={displayName} initials={initials} />
            ) : (
              <img alt='LCW C&Bee' src={toAbsoluteUrl('/media/cbbee/lcw-cbee-logo-cropped.png')} />
            )}
          </div>
          <section className='cbee-auth-form-panel'>
            {currentUser ? (
              <AuthUserSummary profile={profile} onUserOperationsClick={() => setUserOperationsOpen(true)} />
            ) : (
              <Outlet />
            )}
          </section>
        </aside>
        {userOperationsOpen && (
          <div
            className='cbee-profile-editor-overlay'
            role='presentation'
            onMouseDown={() => setUserOperationsOpen(false)}
          >
            <aside
              ref={profileEditorRef}
              className='cbee-profile-editor-drawer cbee-auth-sidebar'
              aria-label='Kullanıcı işlemleri'
              onMouseDown={(event) => event.stopPropagation()}
            >
              <button
                type='button'
                className='cbee-profile-editor-close'
                aria-label='Kapat'
                onClick={() => setUserOperationsOpen(false)}
              >
                ×
              </button>
              <Login
                initialView='userOperationsProfile'
                onProfileComplete={() => {
                  setUserOperationsOpen(false)
                }}
              />
            </aside>
          </div>
        )}
        <main className='cbee-auth-blank' aria-hidden='true' />
      </div>
    </ThemeModeProvider>
  )
}

const AuthPage = () => (
  <Routes>
    <Route element={<AuthLayout />}>
      <Route path='login' element={<Login />} />
      <Route path='autologin/:token' element={<AutoLogin />} />
      <Route path='forgot-password' element={<ForgotPassword />} />
      <Route path='registration' element={<Registration />} />
      <Route index element={<Login />} />
    </Route>
  </Routes>
)

export { AuthPage }
